<template>
  <div class="employee-archive" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <div class="dashboard-content">
      <div class="page-container">
        <div class="modern-page-header">
          <div class="header-content">
            <div class="title-section">
              <h1 class="dashboard-title">Employee Archive</h1>
              <p class="dashboard-subtitle">
                <span class="icon">📦</span>
                Terminated, resigned and retired employees
              </p>
            </div>
            <div class="header-actions">
              <button class="refresh-btn" @click="loadArchived" :disabled="loading">
                <i class="bx bx-refresh" :class="{ spin: loading }"></i>
                {{ loading ? 'Loading...' : 'Refresh' }}
              </button>
              <button class="action-btn primary" @click="goToRegister">
                <i class="bx bx-group"></i>
                Active Register
              </button>
            </div>
          </div>

          <div class="breadcrumb-section">
            <div class="breadcrumb">
              <span class="breadcrumb-item" @click="goToRegister">
                <span class="icon">👥</span>
                <span class="text">Employee Register</span>
              </span>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Archive</span>
            </div>
          </div>
        </div>

        <div class="metrics-grid">
          <div class="metric-card gradient-2">
            <div class="metric-icon"><i class="bx bx-archive-in"></i></div>
            <div class="metric-content">
              <p class="metric-label">Separated employees</p>
              <h2 class="metric-value">{{ totalItems }}</h2>
            </div>
          </div>
          <div class="metric-card gradient-1">
            <div class="metric-icon"><i class="bx bx-lock-alt"></i></div>
            <div class="metric-content">
              <p class="metric-label">Account access</p>
              <h2 class="metric-value metric-value-text">Disabled</h2>
            </div>
          </div>
        </div>

        <div class="filters-section">
          <div class="filters-header">
            <h3><i class="bx bx-filter-alt"></i> Search archive</h3>
          </div>
          <div class="filters-grid">
            <div class="search-box">
              <i class="bx bx-search"></i>
              <input
                v-model="search"
                type="text"
                placeholder="Search by name, employee no., national ID, email..."
                @input="debounceSearch"
              >
              <button v-if="search" class="clear-btn" @click="clearSearch"><i class="bx bx-x"></i></button>
            </div>
            <div class="filter-group">
              <label><i class="bx bx-user-circle"></i> Status</label>
              <select v-model="statusFilter" class="form-select" @change="onFilterChange">
                <option value="">All separated</option>
                <option value="Terminated">Terminated</option>
                <option value="Resigned">Resigned</option>
                <option value="Retired">Retired</option>
              </select>
            </div>
            <div class="filter-group">
              <label><i class="bx bx-building"></i> Department</label>
              <select v-model="departmentFilter" class="form-select" @change="onFilterChange">
                <option value="">All departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.departmentName }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="employees-section">
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading archived employees...</p>
          </div>

          <div v-else-if="employees.length === 0" class="empty-state">
            <div class="empty-icon"><i class="bx bx-archive"></i></div>
            <h3>No separated employees</h3>
            <p>Terminated, resigned and retired employees will appear here.</p>
            <button class="action-btn primary" @click="goToRegister">Go to active register</button>
          </div>

          <div v-else class="employees-table">
            <div class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Archived</th>
                    <th>Reason</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="employee in employees" :key="employee.id">
                    <td>
                      <div class="employee-cell">
                        <div class="avatar">{{ getInitials(employee) }}</div>
                        <div>
                          <h6>{{ getFullName(employee) }}</h6>
                          <small>{{ employee.empNo }}</small>
                        </div>
                      </div>
                    </td>
                    <td>{{ employee.departmentName || '—' }}</td>
                    <td><span class="status-pill" :class="statusClass(employee.status)">{{ employee.status }}</span></td>
                    <td>
                      <div>{{ formatDate(employee.archived_at) }}</div>
                      <small v-if="employee.archived_by" class="text-muted">by {{ employee.archived_by }}</small>
                    </td>
                    <td class="reason-cell">{{ employee.archive_reason || '—' }}</td>
                    <td class="text-center">
                      <button class="btn btn-sm btn-icon" title="View" @click="viewDetails(employee)">
                        <i class="bx bx-show"></i>
                      </button>
                      <button class="btn btn-sm btn-icon restore-btn" title="Restore to register" @click="openRestoreModal(employee)">
                        <i class="bx bx-undo"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="employees.length" class="pagination-section">
            <div class="pagination-info">
              Showing {{ startIndex + 1 }} to {{ Math.min(endIndex, totalItems) }} of {{ totalItems }}
            </div>
            <div class="pagination-controls">
              <button class="pagination-btn" :disabled="currentPage === 1" @click="prevPage">
                <i class="bx bx-chevron-left"></i>
              </button>
              <button
                v-for="page in visiblePages"
                :key="'p-' + page"
                class="pagination-btn"
                :class="{ active: page === currentPage, disabled: page === '...' }"
                :disabled="page === '...'"
                @click="page !== '...' && goToPage(page)"
              >
                {{ page }}
              </button>
              <button class="pagination-btn" :disabled="currentPage === totalPages" @click="nextPage">
                <i class="bx bx-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="showRestoreModal && selectedEmployee" class="archive-modal-overlay" @click.self="closeRestoreModal">
        <div class="archive-modal-card">
          <h5>Restore employee</h5>
          <p>
            Return <strong>{{ getFullName(selectedEmployee) }}</strong> to the active employee register?
          </p>
          <label class="restore-check">
            <input v-model="reactivateUser" type="checkbox">
            <span>Reactivate linked user login</span>
          </label>
          <div class="archive-modal-actions">
            <button class="action-btn" @click="closeRestoreModal">Cancel</button>
            <button class="action-btn primary" :disabled="restoring" @click="confirmRestore">
              {{ restoring ? 'Restoring...' : 'Restore' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/Navigation";
import { authUrl, execute } from "@/api";
import moment from "moment";

export default {
  name: "EmployeeArchive",
  components: { Navigation },

  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      restoring: false,
      employees: [],
      departments: [],
      search: "",
      statusFilter: "",
      departmentFilter: "",
      currentPage: 1,
      pageSize: 20,
      totalItems: 0,
      searchTimeout: null,
      selectedEmployee: null,
      showRestoreModal: false,
      reactivateUser: false
    };
  },

  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    },
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return this.startIndex + this.employees.length;
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else if (current <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push("...");
        pages.push(total);
      } else if (current >= total - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = total - 4; i <= total; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(total);
      }
      return pages;
    }
  },

  mounted() {
    this.getDepartments();
    this.loadArchived();
  },

  methods: {
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
        .then((res) => {
          if (res.data?.success) {
            this.departments = res.data.data?.departments || res.data.data || [];
          }
        })
        .catch(() => {});
    },

    loadArchived() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getEmployees");
      data.append("archived", "1");
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("keyword", this.search);
      if (this.departmentFilter) data.append("department", this.departmentFilter);
      if (this.statusFilter) data.append("status", this.statusFilter);

      execute(data, authUrl)
        .then((res) => {
          if (res.data?.success) {
            this.employees = res.data.data?.employees || [];
            this.totalItems = res.data.data?.totalItems || 0;
          }
        })
        .catch((e) => console.error(e))
        .finally(() => {
          this.loading = false;
        });
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadArchived();
      }, 400);
    },

    clearSearch() {
      this.search = "";
      this.currentPage = 1;
      this.loadArchived();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadArchived();
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.loadArchived();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.loadArchived();
      }
    },

    goToPage(page) {
      this.currentPage = page;
      this.loadArchived();
    },

    viewDetails(employee) {
      sessionStorage.setItem("employeeId", employee.id);
      this.$router.push("/employee-details-view");
    },

    openRestoreModal(employee) {
      this.selectedEmployee = employee;
      this.reactivateUser = false;
      this.showRestoreModal = true;
    },

    closeRestoreModal() {
      this.showRestoreModal = false;
      this.selectedEmployee = null;
    },

    confirmRestore() {
      if (!this.selectedEmployee) return;
      this.restoring = true;
      const data = new FormData();
      data.append("function", "restoreArchivedEmployee");
      data.append("employeeId", this.selectedEmployee.id);
      data.append("reactivateUser", this.reactivateUser ? "1" : "0");

      execute(data, authUrl)
        .then((res) => {
          if (res.data?.success) {
            this.$toast?.success(res.data.message || "Employee restored") || alert("Employee restored");
            this.closeRestoreModal();
            this.loadArchived();
          } else {
            throw new Error(res.data?.message || "Restore failed");
          }
        })
        .catch((e) => {
          this.$toast?.error(e.message) || alert(e.message);
        })
        .finally(() => {
          this.restoring = false;
        });
    },

    goToRegister() {
      this.$router.push({ name: "employee-register" });
    },

    getFullName(employee) {
      return [employee.firstName, employee.middleName, employee.lastName].filter(Boolean).join(" ");
    },

    getInitials(employee) {
      return `${(employee.firstName || "?")[0]}${(employee.lastName || "")[0] || ""}`.toUpperCase();
    },

    formatDate(value) {
      if (!value) return "—";
      return moment(value).format("DD MMM YYYY");
    },

    statusClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "terminated") return "status-terminated";
      if (s === "resigned") return "status-resigned";
      if (s === "retired") return "status-retired";
      return "status-default";
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.employee-archive {
  min-height: 100vh;
}

.dashboard-content {
  margin-left: 280px;
  padding: 20px;
  min-height: 100vh;
  transition: margin-left 0.3s ease;
}

.employee-archive.nav-collapsed .dashboard-content {
  margin-left: 70px;
}

@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
  }
}

.page-container {
  max-width: 1200px;
}

.modern-page-header {
  margin-bottom: 1.5rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.dashboard-title {
  margin: 0;
  font-size: 1.65rem;
  font-weight: 700;
  color: #0f172a;
}

.dashboard-subtitle {
  margin: 0.35rem 0 0;
  color: #64748b;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.refresh-btn,
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
}

.action-btn.primary,
.refresh-btn {
  background: linear-gradient(135deg, #4f46e5, #6366f1);
  color: #fff;
  border: none;
}

.breadcrumb-section {
  margin-top: 0.75rem;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: #94a3b8;
}

.breadcrumb-item {
  cursor: pointer;
  color: #6366f1;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.metric-card {
  display: flex;
  gap: 0.85rem;
  align-items: center;
  padding: 1rem;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
}

.metric-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  background: #eef2ff;
  color: #4f46e5;
}

.metric-label {
  margin: 0;
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
}

.metric-value {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.metric-value-text {
  font-size: 1.1rem;
}

.filters-section,
.employees-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1rem 1.15rem;
  margin-bottom: 1rem;
}

.filters-header h3 {
  margin: 0 0 1rem;
  font-size: 1rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
}

.search-box {
  position: relative;
}

.search-box i.bx-search {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-box input {
  width: 100%;
  padding: 0.6rem 2rem 0.6rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  cursor: pointer;
}

.filter-group label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.35rem;
}

.form-select {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #64748b;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 1rem;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
  vertical-align: middle;
}

.employee-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #475569;
}

.employee-cell h6 {
  margin: 0;
}

.reason-cell {
  max-width: 220px;
  font-size: 0.875rem;
  color: #64748b;
}

.status-pill {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.status-terminated { background: #fee2e2; color: #b91c1c; }
.status-resigned { background: #fef3c7; color: #b45309; }
.status-retired { background: #e0f2fe; color: #0369a1; }
.status-default { background: #f1f5f9; color: #475569; }

.btn-icon {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  padding: 0.35rem 0.5rem;
  cursor: pointer;
  margin: 0 2px;
}

.restore-btn {
  color: #059669;
  border-color: #bbf7d0;
}

.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.pagination-controls {
  display: flex;
  gap: 0.35rem;
}

.pagination-btn {
  min-width: 36px;
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}

.pagination-btn.active {
  background: #6366f1;
  color: #fff;
  border-color: #6366f1;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.archive-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.archive-modal-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem;
  width: min(440px, 100%);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
}

.archive-modal-card h5 {
  margin: 0 0 0.75rem;
}

.archive-modal-card p {
  color: #64748b;
  margin: 0 0 1rem;
}

.restore-check {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
}

.archive-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.spin {
  animation: spin 0.7s linear infinite;
}

@media (max-width: 992px) {
  .metrics-grid,
  .filters-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 576px) {
  .metrics-grid,
  .filters-grid {
    grid-template-columns: 1fr;
  }
}
</style>
